import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api/apiClient';
import {
  CreateOpportunityAnalysisResponse,
  CreateOpportunityRequest,
  CreateOpportunityResponse,
  GetOpportunityResponse,
  InvestorOpportunitySummaryDTO,
  UserRole,
  OpportunityInactivityReason,
  OpportunityDTO,
  MediaType,
  ListOpportunityMediaResponse,
  UpdateOpportunityRequest,
  OpportunityFilters,
} from '@inversor-hub/types';
import { AxiosResponse } from 'axios';
import { LIST_INVESTORS_OPPORTUNITY_INTERESTS } from '@/lib/api/opportunity-interests';

export const LIST_OPPORTUNITIES_KEY = 'opportunities';
const GET_OPPORTUNITY_KEY = 'opportunity';
export const LIST_OPPORTUNITY_MEDIA_KEY = 'opportunity-media';

export function useCreateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (opportunityParams: CreateOpportunityRequest) => {
      const createOpportunityResponse = await apiClient.post<
        unknown,
        AxiosResponse<CreateOpportunityResponse>,
        CreateOpportunityRequest
      >('/opportunities', opportunityParams);
      return createOpportunityResponse.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}

export function useGetOpportunity(opportunityId?: string) {
  return useQuery({
    queryKey: [GET_OPPORTUNITY_KEY, opportunityId],
    queryFn: async () => {
      return apiClient.get<GetOpportunityResponse>(`/opportunities/${opportunityId}`);
    },
    select: (response) => response.data.data,
    enabled: !!opportunityId,
  });
}

type OpportunityResponseByRole<R extends UserRole> = R extends 'investor'
  ? InvestorOpportunitySummaryDTO
  : OpportunityDTO;

interface ListOpportunitiesFilter extends OpportunityFilters {
  hasInterest?: boolean;
}

export function useListOpportunities<R extends UserRole>(
  filter?: ListOpportunitiesFilter,
  options?: { enabled?: boolean }
) {
  const queryParams = new URLSearchParams();
  if (filter?.hasInterest) queryParams.append('hasInterest', filter.hasInterest.toString());
  if (filter?.province) queryParams.append('province', filter.province);
  if (filter?.city) queryParams.append('city', filter.city);
  if (filter?.rooms !== undefined) queryParams.append('rooms', filter.rooms.toString());
  if (filter?.bathrooms !== undefined) queryParams.append('bathrooms', filter.bathrooms.toString());
  if (filter?.type) queryParams.append('type', filter.type);

  const queryString = queryParams.toString();
  const url = queryString ? `/opportunities?${queryString}` : '/opportunities';

  return useQuery({
    queryKey: [LIST_OPPORTUNITIES_KEY, queryString],
    queryFn: async () => {
      return apiClient.get<{ data: Array<OpportunityResponseByRole<R>> }>(url);
    },
    select: (response) => response.data.data,
    enabled: options?.enabled,
  });
}

export function useUpdateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ changeset, id }: { changeset: UpdateOpportunityRequest; id: string }) => {
      await apiClient.patch<unknown, unknown, UpdateOpportunityRequest>(
        `/opportunities/${id}`,
        changeset
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_OPPORTUNITY_KEY, variables.id] });
    },
  });
}

export function useDeleteOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/opportunities/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}

export function useUploadOpportunityMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      file,
      fileType,
    }: {
      opportunityId: string;
      file: File;
      fileType: MediaType;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);

      await apiClient.post(`/opportunities/${opportunityId}/media`, formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LIST_OPPORTUNITY_MEDIA_KEY, variables.opportunityId],
      });
    },
  });
}

export function useGetOpportunityMedia(opportunityId?: string) {
  return useQuery({
    queryKey: [LIST_OPPORTUNITY_MEDIA_KEY, opportunityId],
    queryFn: async () => {
      return apiClient.get<ListOpportunityMediaResponse>(`/opportunities/${opportunityId}/media`);
    },
    select: (response) => response.data.data,
    enabled: !!opportunityId,
  });
}

export function useDeleteOpportunityMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/opportunity-media/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [LIST_OPPORTUNITY_MEDIA_KEY],
      });
    },
  });
}

export function useReserveOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/opportunities/${id}/reserve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTORS_OPPORTUNITY_INTERESTS] });
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}

export function useRejectOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/opportunities/${id}/reject`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTORS_OPPORTUNITY_INTERESTS] });
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}

export function useCreateOpportunityAnalysis() {
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      const { data } = await apiClient.post<CreateOpportunityAnalysisResponse>(
        `/opportunities/${opportunityId}/analysis`
      );
      return data.data.analysis;
    },
  });
}

export function usePublishOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      await apiClient.post(`/opportunities/${opportunityId}/publish`);
    },
    onSuccess: (_, opportunityId) => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_OPPORTUNITY_KEY, opportunityId] });
    },
  });
}

export function useHideOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      await apiClient.post(`/opportunities/${opportunityId}/hide`);
    },
    onSuccess: (_, opportunityId) => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_OPPORTUNITY_KEY, opportunityId] });
    },
  });
}

export function useShowOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      await apiClient.post(`/opportunities/${opportunityId}/show`);
    },
    onSuccess: (_, opportunityId) => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_OPPORTUNITY_KEY, opportunityId] });
    },
  });
}

export function useDeactivateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      reason,
      otherReason,
    }: {
      opportunityId: string;
      reason: OpportunityInactivityReason;
      otherReason?: string;
    }) => {
      await apiClient.post(`/opportunities/${opportunityId}/deactivate`, {
        reason,
        otherReason,
      });
    },
    onSuccess: (_, { opportunityId }) => {
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_OPPORTUNITY_KEY, opportunityId] });
    },
  });
}
